/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import PrePurchaseUpsellAllPlansModal from 'bundles/coursera-plus-common/components/prePurchaseUpsell/PrePurchaseUpsellAllPlansModal';
import {
  getFormattedPartnerDisplayName,
  usePrePurchaseUpsellEnrollment,
} from 'bundles/coursera-plus-common/utils/prePurchaseUpsellUtils';
import { PURCHASE_SINGLE_COURSE } from 'bundles/enroll-course/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-course/lib/enrollmentChoiceUtils';
import EnrollErrorModal from 'bundles/enroll/components/common/EnrollErrorModal';
import PromotionApplicableCheckoutMessage from 'bundles/enroll/components/common/PromotionApplicableCheckoutMessage';
import { useCourseEnrollModalData } from 'bundles/enroll/data/usePageData';
import { EnrollModalType } from 'bundles/enroll/types/modalTypes';
import type { ApiError } from 'bundles/enroll/utils/errorUtils';
import { useEnrollModalEventing } from 'bundles/enroll/utils/eventingUtils';
import ReactPriceDisplay from 'bundles/payments-common/components/ReactPriceDisplay';

import _t from 'i18n!nls/enroll-course';

const getBulletPoints = () => [
  /* eslint-disable no-restricted-syntax */
  _t('Access to course materials, including videos, readings, and discussion forums'),
  _t('Access to graded assignments'),
  _t('Final grade at the end of the course'),
  _t('Shareable Course Certificate'),
  /* eslint-enable no-restricted-syntax */
];

type PropsFromCaller = {
  onClose: () => void;
};

const styles = {
  bulletPoint: css`
    display: grid;
    gap: var(--cds-spacing-100);
    grid-template-columns: auto 1fr;
    margin-bottom: var(--cds-spacing-200);
  `,
};

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const ClosedCourseEnrollModal = ({ onClose }: PropsFromCaller) => {
  const [didClickContinue, setDidClickContinue] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [activeModal, setActiveModal] = useState<EnrollModalType>(EnrollModalType.ENROLL);
  const [step, setStep] = useState<1 | 2>(1);

  const { promotionEligibilities, course, productPrice, partners } = useCourseEnrollModalData();
  const { additionalParams } = usePrePurchaseUpsellEnrollment({});

  const { getEnrollModalTrackingRef, trackEnrollModalContinue, trackEnrollModalClose, trackEnrollModalSecondaryCta } =
    useEnrollModalEventing({ isUsingPrePurchaseUpsellFlow: true });
  const visibilityTrackingRef = getEnrollModalTrackingRef();
  const enrollmentChoiceType = PURCHASE_SINGLE_COURSE;

  const handleClose = () => {
    trackEnrollModalClose();
    onClose();
  };

  const handleContinue = () => {
    trackEnrollModalContinue(enrollmentChoiceType);
    setDidClickContinue(true);

    const promoCode = promotionEligibilities?.isEligible ? promotionEligibilities.promoCodeId : undefined;
    const options = { courseId: course.id };

    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[enrollmentChoiceType];
    submitEnrollmentPromise({ handleSubmitPromise, options, promoCode, additionalParams }).catch(
      (apiError: ApiError) => {
        setActiveModal(EnrollModalType.ERROR);
        setError(apiError);
      }
    );
  };

  const showViewAllPlans = () => {
    setStep(2);
    trackEnrollModalSecondaryCta('view_all_plans');
  };

  const { HeadingGroup, Content, Actions } = Dialog;

  if (step === 2) {
    return <PrePurchaseUpsellAllPlansModal onClose={() => setStep(1)} isStandAloneCourse={true} />;
  }

  switch (activeModal) {
    case EnrollModalType.ERROR:
      return <EnrollErrorModal error={error} onClose={handleClose} />;
    case EnrollModalType.ENROLL:
      return (
        <Dialog open={true} onClose={handleClose} sticky="actions" ref={visibilityTrackingRef}>
          <HeadingGroup>{course.name}</HeadingGroup>
          <Content>
            {partners && (
              <Typography2
                variant="subtitleMedium"
                component="p"
                css={css`
                  margin-top: -26px;
                  margin-bottom: var(--cds-spacing-400);
                `}
              >
                {getFormattedPartnerDisplayName(partners)}
              </Typography2>
            )}
            {productPrice && (
              <Typography2
                component="p"
                variant="subtitleMedium"
                css={css`
                  margin-bottom: var(--cds-spacing-300);
                `}
              >
                <FormattedMessage
                  // eslint-disable-next-line no-restricted-syntax
                  message={_t('This course costs {price} - your course fee includes:')}
                  price={<ReactPriceDisplay value={productPrice.finalAmount} currency={productPrice.currencyCode} />}
                />
              </Typography2>
            )}

            <ul>
              {getBulletPoints().map((bulletPoint) => (
                <li key={bulletPoint} css={styles.bulletPoint}>
                  <CheckIcon size="large" color="interactive" />
                  <Typography2 component="span">{bulletPoint}</Typography2>
                </li>
              ))}
            </ul>

            <PromotionApplicableCheckoutMessage course={course} />
          </Content>
          <Actions>
            <TrackedButton
              variant="primary"
              onClick={handleContinue}
              loading={didClickContinue}
              trackingName="closed_course_enroll_modal_continue_button"
              data-testid="closed-course-enroll-modal-continue-button"
            >
              {_t('Continue')}
            </TrackedButton>
            <TrackedButton
              variant="secondary"
              css={css`
                margin-left: var(--cds-spacing-200);
              `}
              onClick={showViewAllPlans}
              trackingName="pre_purchase_upsell_course_enroll_modal_view_all_plans_cta"
              data-testid="pre-purchase-upsell-course-enroll-modal-view-all-plans-cta"
            >
              {_t('View all plans')}
            </TrackedButton>
          </Actions>
        </Dialog>
      );
    default:
      return null;
  }
};

export default ClosedCourseEnrollModal;
